/**
 * メールアドレス一覧 + 作成ウィザード（05-ui.md §8.4）。
 *
 * 作成は「アドレス → 容量 → パスワード」の 3 ステップ。ステップを分けるのは、
 * 1 画面に全部出すと契約者が最初の入力で止まってしまうため。
 * 作成後は **接続情報を必ず表示する**（ここで控えないと、後から
 * 「メールソフトに何を入れればいいのか」で必ず問い合わせになる）。
 */
import * as React from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Alert,
  AlertDescription,
  AlertTitle,
  Badge,
  Button,
  CopyableCode,
  CursorPagination,
  DataTable,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  EmptyState,
  Field,
  Input,
  PageHeader,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  StatusBadge,
  UsageBar,
  formatBytes,
  useCursorPagination,
  type DataTableColumn,
} from '@mail-sv/ui';
import { AtSign, FileDown, Plus, Printer, Search } from 'lucide-react';
import { api, newIdempotencyKey } from '../lib/api.js';
import { qk } from '../lib/query-keys.js';
import { useDomains, useOrg } from '../lib/hooks.js';
import { AppLink, useAppNavigate } from '../lib/nav.js';
import { FormAlert, SubmitBar } from '../components/form.js';
import { PasswordField, generatePassword } from '../components/password-field.js';
import { PlanLimitAlert, PlanLimitFromError, isAtLimit } from '../components/plan-limit.js';
import { JobProgressBar, useJobProgress } from '../components/job-progress.js';
import { HelpNote } from '../components/help.js';
import { buildMailClientSettings } from '../lib/mail-client-settings.js';
import { bytesToGib, formatQuota, gibToBytes } from '../lib/format.js';
import { useAuth, canManageOrg } from '../stores/auth.js';
import type { CursorPage, Mailbox, MailboxCreateRequest, MailboxCreated } from '../lib/api-types.js';

export function MailboxesPage() {
  const org = useOrg();
  const domains = useDomains();
  const { user } = useAuth();
  const [q, setQ] = React.useState('');
  const [domainId, setDomainId] = React.useState('all');
  const [createOpen, setCreateOpen] = React.useState(false);
  const page = useCursorPagination(50);

  const filter = { q: q.trim(), domainId, limit: page.limit, cursor: page.cursor };

  const list = useQuery({
    queryKey: qk.mailboxes(filter),
    queryFn: () => {
      const params = new URLSearchParams();
      params.set('limit', String(page.limit));
      if (page.cursor) params.set('cursor', page.cursor);
      if (filter.q) params.set('q', filter.q);
      if (domainId !== 'all') params.set('domainId', domainId);
      return api.get<CursorPage<Mailbox>>(`/mailboxes?${params.toString()}`);
    },
    staleTime: 15_000,
  });

  /*
   * **設定情報シートの一括印刷のための選択状態（W-ONBOARD §3-4）。**
   *
   * 実務では「今月入社する 5 人ぶんをまとめて刷る」形になるので、
   * 1 件ずつ詳細を開いて印刷するのは現実的でない。ページをまたいで
   * 選び続けられるよう、`page.cursor` が変わっても選択は捨てない
   * （表示されていない ID が残っても、印刷側が存在しない ID を落とす）。
   */
  const [selected, setSelected] = React.useState<readonly string[]>([]);
  const rows = list.data?.items ?? [];
  // 依存配列に式を直接書かないため、ID の並びを 1 本の文字列に落としてから memo する。
  const pageIdKey = rows.map((r) => r.id).join(',');
  const pageIds = React.useMemo(
    () => (pageIdKey === '' ? [] : pageIdKey.split(',')),
    [pageIdKey],
  );
  const allOnPageSelected = pageIds.length > 0 && pageIds.every((id) => selected.includes(id));
  const toggleOne = React.useCallback((id: string) => {
    setSelected((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  }, []);
  const togglePage = React.useCallback(() => {
    setSelected((prev) =>
      pageIds.every((id) => prev.includes(id))
        ? prev.filter((id) => !pageIds.includes(id))
        : [...prev, ...pageIds.filter((id) => !prev.includes(id))],
    );
  }, [pageIds]);

  const canWrite = canManageOrg(user);
  const mailboxesMax = org.data?.plan.limits.mailboxesMax ?? 0;
  const mailboxesUsed = org.data?.usage.mailboxes ?? 0;
  const atLimit = isAtLimit(mailboxesUsed, mailboxesMax);
  const hasDomain = (domains.data?.items.length ?? 0) > 0;

  const columns: Array<DataTableColumn<Mailbox>> = [
    {
      id: 'select',
      // ヘッダのチェックは「このページを全選択」。全ページではない
      // （次ページの件数を知らないまま「全部選んだ」と言わない）。
      header: (
        <label className="flex cursor-pointer items-center gap-2 text-xs font-normal">
          <input
            type="checkbox"
            className="size-4"
            checked={allOnPageSelected}
            onChange={togglePage}
            aria-label="このページのメールアドレスをすべて選択"
          />
          <span className="sr-only">選択</span>
        </label>
      ),
      width: 'w-10',
      cell: (row) => (
        <input
          type="checkbox"
          className="size-4"
          checked={selected.includes(row.id)}
          onChange={() => toggleOne(row.id)}
          aria-label={`${row.address} を選択`}
        />
      ),
    },
    {
      id: 'address',
      header: 'メールアドレス',
      cell: (row) => (
        <div className="min-w-0">
          <AppLink to={`/mailboxes/${row.id}`} className="block truncate font-medium text-foreground hover:underline">
            {row.address}
          </AppLink>
          {row.displayName ? (
            <p className="truncate text-xs text-muted-foreground">{row.displayName}</p>
          ) : null}
        </div>
      ),
    },
    {
      id: 'usage',
      header: '使用容量',
      width: 'w-56',
      hideBelow: 'md',
      // `quotaBytes` が null なのは「プラン既定を継承」の意味。
      // バーの母数はプラン既定値で描く。
      cell: (row) => (
        <UsageBar
          used={row.usedBytes}
          total={row.quotaBytes ?? (org.data?.plan.limits.mailboxQuotaBytesDefault ?? 0)}
          formatValue={(n) => formatBytes(n)}
        />
      ),
    },
    {
      id: 'features',
      header: '設定',
      width: 'w-40',
      hideBelow: 'lg',
      cell: (row) => (
        <div className="flex flex-wrap gap-1">
          {row.forwardCount > 0 ? (
            <Badge variant="muted" size="sm">
              転送 {row.forwardCount}
            </Badge>
          ) : null}
          {row.autoresponderEnabled ? (
            <Badge variant="info" size="sm">
              自動応答
            </Badge>
          ) : null}
          {row.webmailEnabled ? (
            <Badge variant="secondary" size="sm">
              Webメール
            </Badge>
          ) : null}
        </div>
      ),
    },
    {
      id: 'status',
      header: '状態',
      width: 'w-32',
      cell: (row) => <StatusBadge status={row.status} dot size="sm" />,
    },
  ];

  return (
    <>
      <PageHeader
        title="メールアドレス"
        description="info@example.jp のようなメールアドレスを作成・管理します。"
        actions={
          canWrite ? (
            <Button
              onClick={() => setCreateOpen(true)}
              disabled={atLimit || !hasDomain}
              title={
                !hasDomain
                  ? '先にドメインを追加してください'
                  : atLimit
                    ? 'プランの上限に達しています'
                    : undefined
              }
            >
              <Plus aria-hidden />
              メールアドレスを作成
            </Button>
          ) : null
        }
      />

      <div className="space-y-4">
        <PlanLimitAlert
          resourceLabel="メールアドレス"
          current={mailboxesUsed}
          max={mailboxesMax}
          canUpgrade={canManageOrg(user)}
        />

        {!hasDomain ? (
          <Alert variant="info">
            <AlertTitle>先にドメインの登録が必要です</AlertTitle>
            <AlertDescription className="space-y-2">
              <p>メールアドレスを作るには、まず「@」より後ろに使うドメインを登録してください。</p>
              <Button asChild variant="outline" size="sm">
                <AppLink to="/domains">ドメインを追加する</AppLink>
              </Button>
            </AlertDescription>
          </Alert>
        ) : null}

        <div className="flex flex-wrap items-end gap-3">
          <Field label="アドレスで検索" className="min-w-[14rem] flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" aria-hidden />
              <Input
                value={q}
                onChange={(e) => {
                  setQ(e.target.value);
                  page.reset();
                }}
                placeholder="info"
                className="pl-9"
              />
            </div>
          </Field>
          <Field label="ドメイン" className="min-w-[12rem]">
            <Select
              value={domainId}
              onValueChange={(v) => {
                setDomainId(v);
                page.reset();
              }}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">すべて</SelectItem>
                {(domains.data?.items ?? []).map((d) => (
                  <SelectItem key={d.id} value={d.id}>
                    {d.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>
        </div>

        <SelectedSheetsBar
          selected={selected}
          canExport={canWrite}
          onClear={() => setSelected([])}
        />

        <DataTable
          columns={columns}
          rows={rows}
          rowKey={(row) => row.id}
          loading={list.isPending}
          error={list.isError ? '一覧を取得できませんでした。' : undefined}
          empty={
            q || domainId !== 'all' ? (
              <EmptyState
                title="条件に一致するメールアドレスがありません"
                description="検索条件を変えてお試しください。"
                action={
                  <Button
                    variant="outline"
                    onClick={() => {
                      setQ('');
                      setDomainId('all');
                      page.reset();
                    }}
                  >
                    条件をクリア
                  </Button>
                }
              />
            ) : (
              <EmptyState
                icon={AtSign}
                title="メールアドレスを作成しましょう"
                description="info@ や support@ など、実際に使うメールアドレスを作成します。作成後すぐにメールソフトや Webメールで使えます。"
                action={
                  canWrite && hasDomain ? (
                    <Button onClick={() => setCreateOpen(true)}>
                      <Plus aria-hidden />
                      メールアドレスを作成
                    </Button>
                  ) : null
                }
              />
            )
          }
          footer={
            <CursorPagination
              nextCursor={list.data?.nextCursor ?? null}
              canGoPrev={page.canGoPrev}
              onNext={() => page.goNext(list.data?.nextCursor ?? null)}
              onPrev={page.goPrev}
              limit={page.limit}
              onLimitChange={page.setLimit}
              currentCount={list.data?.items.length}
              loading={list.isFetching}
            />
          }
        />

        <BulkPasswordNote canWrite={canWrite} />
      </div>

      <CreateMailboxDialog open={createOpen} onOpenChange={setCreateOpen} />
    </>
  );
}

/**
 * 選択したメールアドレスの設定情報シートを一括で印刷する帯（§3-4）。
 *
 * **一覧の上に固定で出さない。** 1 件も選んでいないときに帯があると、
 * 一覧の主目的（探す）を毎回押しのけることになる。選んだ瞬間にだけ現れる。
 *
 * 印刷画面は**別タブで開く**。同じタブで遷移すると、印刷して戻ってきたときに
 * 検索条件とページ位置が消えて、続きの人ぶんを選び直すことになる。
 */
function SelectedSheetsBar({
  selected,
  canExport,
  onClear,
}: {
  selected: readonly string[];
  /** PDF の申し込みは `org.export.write` 相当（＝管理者）だけに出す。 */
  canExport: boolean;
  onClear: () => void;
}) {
  const [requested, setRequested] = React.useState(false);

  /*
   * **PDF は非同期**（`POST /exports` → ワーカーが生成 → 「エクスポート」画面で
   * ダウンロード）。同期で返せないのは、100 件ぶんのフォント埋め込み PDF を
   * リクエストの中で作ると API のイベントループを長時間止めるため。
   * 数件なら上の印刷ボタン（ブラウザの印刷ダイアログ → PDF 保存）のほうが速い。
   */
  const requestPdf = useMutation({
    mutationFn: () =>
      api.post<{ exportId: string }>('/exports', {
        kind: 'setup_sheets',
        format: 'pdf',
        mailboxIds: [...selected],
      }),
    onSuccess: () => setRequested(true),
  });

  if (selected.length === 0) return null;
  const href = `/print/mail-setup?ids=${encodeURIComponent(selected.join(','))}`;

  return (
    <div className="space-y-2 rounded-lg border border-border bg-panel px-4 py-3">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm">
          <span className="font-medium">{selected.length} 件</span> を選択しています。
          メールソフトの設定情報を 1 件 1 枚で出力できます（パスワードは印字されません）。
        </p>
        <div className="flex flex-wrap gap-2">
          <Button variant="ghost" size="sm" onClick={onClear}>
            選択を解除
          </Button>
          {canExport ? (
            <Button
              variant="outline"
              size="sm"
              onClick={() => requestPdf.mutate()}
              loading={requestPdf.isPending}
            >
              <FileDown aria-hidden />
              PDF をまとめて作る
            </Button>
          ) : null}
          <Button asChild size="sm">
            {/*
              `AppLink` ではなく素の `<a target="_blank">` を使う。ルーター内の
              遷移にすると同じタブで開いてしまい、一覧の状態が失われる。
            */}
            <a href={href} target="_blank" rel="noopener noreferrer">
              <Printer aria-hidden />
              いま印刷する（{selected.length} 件）
            </a>
          </Button>
        </div>
      </div>

      {requested ? (
        <Alert variant="success">
          <AlertTitle>PDF の作成を受け付けました</AlertTitle>
          <AlertDescription>
            できあがると「
            <AppLink to="/exports" className="underline">
              エクスポート
            </AppLink>
            」からダウンロードできます（数分かかります）。
          </AlertDescription>
        </Alert>
      ) : null}
      {requestPdf.isError ? <FormAlert error={requestPdf.error} /> : null}
    </div>
  );
}

/**
 * 一括パスワード再設定への導線。
 *
 * 操作の実体は `/security`（`POST /mailboxes/password-reset-all` は
 * 再認証と確認文字列を要求するので、セキュリティ画面に置いてある）。
 * **探す人はこの一覧に来る**（「メールアドレスの一覧から全部変えたい」）ので、
 * ここから辿れないと 1 件ずつ開いて押し続けることになる。
 * 権限が無いメンバーには出さない（押しても 403 になるだけ）。
 */
function BulkPasswordNote({ canWrite }: { canWrite: boolean }) {
  if (!canWrite) return null;
  return (
    <HelpNote title="乗っ取りが疑われるときは">
      すべてのメールアドレスのパスワードを一度に再設定できます。
      <AppLink to="/security" className="mx-1 text-primary underline-offset-4 hover:underline">
        セキュリティ
      </AppLink>
      の画面の「緊急時の操作」からお進みください。1 件ずつ変更する必要はありません。
    </HelpNote>
  );
}

/* -------------------------------------------------------------- 作成ウィザード */

type Step = 'address' | 'quota' | 'password' | 'done';

function CreateMailboxDialog({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const org = useOrg();
  const domains = useDomains();
  const queryClient = useQueryClient();
  const navigate = useAppNavigate();

  const [step, setStep] = React.useState<Step>('address');
  const [localPart, setLocalPart] = React.useState('');
  const [domainId, setDomainId] = React.useState('');
  const [displayName, setDisplayName] = React.useState('');
  const [quotaGib, setQuotaGib] = React.useState<number>(0);
  const [password, setPassword] = React.useState('');
  const [enableWebmail, setEnableWebmail] = React.useState(true);
  const [jobId, setJobId] = React.useState<string | null>(null);
  /*
   * 作成できたメールボックスの id。**設定情報シートの印刷に要る。**
   * 以前ここを持っておらず、「設定情報を印刷・保存する」が
   * `/mail-clients`（機種別の一般的な手引き）へ飛んでいた。押しても
   * **いま作ったアドレスの設定情報は出てこない**ので、利用者から見れば
   * ボタンが機能していないのと同じだった。
   */
  const [createdId, setCreatedId] = React.useState<string | null>(null);
  const idemKey = React.useRef(newIdempotencyKey());

  const domainList = domains.data?.items ?? [];
  const plan = org.data?.plan;

  // ダイアログを開くたびに初期化する。前回の値が残っていると事故になる。
  React.useEffect(() => {
    if (!open) return;
    setStep('address');
    setLocalPart('');
    setDomainId(domainList[0]?.id ?? '');
    setDisplayName('');
    setQuotaGib(bytesToGib(plan?.limits.mailboxQuotaBytesDefault ?? 0));
    setPassword(generatePassword());
    setEnableWebmail(true);
    setJobId(null);
    setCreatedId(null);
    idemKey.current = newIdempotencyKey();
    // domainList / plan は open の瞬間の値を使えばよい（依存に入れると入力中に初期化される）。
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const domain = domainList.find((d) => d.id === domainId);
  const address = domain ? `${localPart}@${domain.name}` : localPart;

  const create = useMutation({
    // 作成は 202 + `{jobId, mailboxId, address}`。メールボックス本体は返らない。
    mutationFn: (body: MailboxCreateRequest) =>
      api.post<MailboxCreated>('/mailboxes', body, {
        idempotencyKey: idemKey.current,
      }),
    onSuccess: (res) => {
      void queryClient.invalidateQueries({ queryKey: qk.mailboxes() });
      void queryClient.invalidateQueries({ queryKey: qk.org() });
      void queryClient.invalidateQueries({ queryKey: qk.domains() });
      if (res.jobId) setJobId(res.jobId);
      setCreatedId(res.mailboxId ?? null);
      setStep('done');
    },
  });

  const { job } = useJobProgress(jobId);

  // ローカル部の検証。厳密な判定はサーバー側。ここでは明らかな誤りだけ弾く。
  const localOk = /^[a-z0-9]([a-z0-9._-]*[a-z0-9])?$/.test(localPart);
  const quotaMaxGib = bytesToGib(plan?.limits.mailboxQuotaBytesMax ?? 0);
  const quotaOk = quotaGib > 0 && (quotaMaxGib === 0 || quotaGib <= quotaMaxGib);

  const settings =
    org.data && domain
      ? buildMailClientSettings(org.data.node.mailHostname, address)
      : null;

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (create.isPending) return;
        onOpenChange(next);
      }}
    >
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle>
            {step === 'done' ? 'メールアドレスを作成しました' : 'メールアドレスを作成'}
          </DialogTitle>
          <DialogDescription>
            {step === 'address'
              ? '① どんなアドレスにしますか？'
              : step === 'quota'
                ? '② このアドレスで使える容量を決めます'
                : step === 'password'
                  ? '③ メールソフトで使うパスワードを決めます'
                  : '下の設定情報は今すぐ控えてください。パスワードは再表示できません。'}
          </DialogDescription>
        </DialogHeader>

        <PlanLimitFromError error={create.error} />
        {create.isError ? <FormAlert error={create.error} /> : null}

        {step === 'address' ? (
          <form
            className="space-y-4"
            onSubmit={(e) => {
              e.preventDefault();
              setStep('quota');
            }}
          >
            {/*
              **1 つの Field に両方を入れる。**

              以前は「@ より前」と「ドメイン」を別々の Field にして
              `items-end` で並べていた。Field は下にヒントとエラーを持つので、
              **ヒントを持つ左側だけ背が高くなり、ドメインの選択欄が
              1 行ぶん下にずれる**。`@` に `pb-3` を当てて誤魔化してあったが、
              エラーが出るとまたずれる（高さが変わるたびに破綻する）。

              入力は 2 つでも利用者にとっては 1 つの「メールアドレス」なので、
              ラベル・ヒント・エラーを 1 組にまとめる。こうすると高さの差が
              生まれる場所自体が無くなり、`items-center` で素直に揃う。
            */}
            <Field
              label="メールアドレス"
              htmlFor="mailbox-local"
              required
              hint="@ より前は半角の英数字と . _ - が使えます（例: info, sales.jp）"
              error={localPart.length > 0 && !localOk ? '使えない文字が含まれています' : undefined}
            >
              <div className="flex flex-wrap items-center gap-2">
                <Input
                  id="mailbox-local"
                  className="min-w-[10rem] flex-1"
                  value={localPart}
                  onChange={(e) => setLocalPart(e.target.value.toLowerCase())}
                  placeholder="info"
                  autoComplete="off"
                  autoFocus
                />
                <span className="text-lg text-muted-foreground">@</span>
                <Select value={domainId} onValueChange={setDomainId}>
                  <SelectTrigger className="min-w-[12rem] flex-1" aria-label="ドメイン">
                    <SelectValue placeholder="ドメインを選択" />
                  </SelectTrigger>
                  <SelectContent>
                    {domainList.map((d) => (
                      <SelectItem key={d.id} value={d.id}>
                        {d.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </Field>

            <Field
              label="表示名（任意）"
              htmlFor="mailbox-display"
              hint="送信したメールの差出人名として表示されます（例: 株式会社サンプル 営業部）"
            >
              <Input
                id="mailbox-display"
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                autoComplete="off"
              />
            </Field>

            {domain && domain.dnsStatus !== 'ok' ? (
              <Alert variant="warning">
                <AlertTitle>このドメインはまだ DNS 設定が完了していません</AlertTitle>
                <AlertDescription>
                  作成はできますが、DNS の設定が終わるまでメールは届きません。
                </AlertDescription>
              </Alert>
            ) : null}

            <SubmitBar
              submitLabel="次へ"
              onCancel={() => onOpenChange(false)}
              disabled={!localOk || !domainId}
              /*
                **@ より前が埋まるまで出さない。** `address` はドメインを
                選んだ時点で `@example.jp` になるので、そのまま出すと
                「作成するアドレス: @mail-demo…」という**ユーザー名の無い
                アドレス**が最初から見えていた。まだ何も入力していないのに
                作成対象が確定したように見えるので紛らわしい。
              */
              hint={localOk && domain ? `作成するアドレス: ${address}` : undefined}
            />
          </form>
        ) : null}

        {step === 'quota' ? (
          <form
            className="space-y-4"
            onSubmit={(e) => {
              e.preventDefault();
              setStep('password');
            }}
          >
            <Field
              label="このアドレスで使える容量"
              hint={
                plan
                  ? `ご契約プランの上限は 1 アドレスあたり ${formatQuota(plan.limits.mailboxQuotaBytesMax)} です。`
                  : undefined
              }
              error={!quotaOk ? '上限を超えています' : undefined}
            >
              <div className="flex flex-wrap gap-2">
                {[1, 5, 10, 30].map((gib) => (
                  <Button
                    key={gib}
                    type="button"
                    variant={quotaGib === gib ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setQuotaGib(gib)}
                    disabled={quotaMaxGib > 0 && gib > quotaMaxGib}
                  >
                    {gib} GB
                  </Button>
                ))}
                <div className="flex items-center gap-2">
                  <Input
                    type="number"
                    min={1}
                    max={quotaMaxGib > 0 ? quotaMaxGib : undefined}
                    value={quotaGib}
                    onChange={(e) => setQuotaGib(Number(e.target.value))}
                    className="w-24"
                    aria-label="容量（GB）"
                  />
                  <span className="text-sm text-muted-foreground">GB</span>
                </div>
              </div>
            </Field>

            <HelpNote title="容量はあとから変更できます">
              受信したメールがこの容量を超えると、新しいメールを受け取れなくなります。
              迷ったらプランの既定値のままで問題ありません。
            </HelpNote>

            <SubmitBar
              submitLabel="次へ"
              onCancel={() => setStep('address')}
              cancelLabel="戻る"
              disabled={!quotaOk}
            />
          </form>
        ) : null}

        {step === 'password' ? (
          <form
            className="space-y-4"
            onSubmit={(e) => {
              e.preventDefault();
              if (create.isPending) return;
              // リクエストのキー名は API の zod（createSchema）と揃える。
              // `enableWebmail` のような別名で送ると黙って無視され、
              // チェックを外しても Webメールが有効なまま作られる。
              create.mutate({
                localPart,
                domainId,
                password,
                displayName: displayName || undefined,
                quotaBytes: gibToBytes(quotaGib),
                webmailEnabled: enableWebmail,
              });
            }}
          >
            <Field
              label="パスワード"
              hint="メールソフトや Webメールへのログインに使います。自動生成した値をそのまま使うことを推奨します。"
              required
            >
              <PasswordField value={password} onChange={setPassword} />
            </Field>

            <label className="flex items-start gap-2 text-sm">
              <input
                type="checkbox"
                checked={enableWebmail}
                onChange={(e) => setEnableWebmail(e.target.checked)}
                className="mt-1"
              />
              <span>
                Webメール（ブラウザでメールを読む機能）を使えるようにする
                <span className="block text-xs text-muted-foreground">
                  あとから変更できます。メールソフトだけを使う場合はオフでも構いません。
                </span>
              </span>
            </label>

            <Alert variant="warning">
              <AlertTitle>パスワードは作成後に再表示できません</AlertTitle>
              <AlertDescription>
                作成後の画面でコピーするか、控えを保存してください。忘れた場合は再設定になります。
              </AlertDescription>
            </Alert>

            <SubmitBar
              submitLabel="作成する"
              pending={create.isPending}
              disabled={password.length < 8}
              onCancel={() => setStep('quota')}
              cancelLabel="戻る"
            />
          </form>
        ) : null}

        {step === 'done' ? (
          <div className="space-y-4">
            {jobId ? (
              <div className="rounded-lg border border-border bg-panel p-3">
                <JobProgressBar job={job} />
              </div>
            ) : null}

            <CopyableCode label="メールアドレス" value={address} />
            <CopyableCode label="パスワード" value={password} />

            {settings ? (
              <div className="space-y-2 rounded-lg border border-border bg-panel-elevated p-3">
                <p className="text-sm font-medium">メールソフトの設定値</p>
                <CopyableCode label="受信サーバー（IMAP）" value={`${settings.host} : 993 (SSL/TLS)`} />
                <CopyableCode label="送信サーバー（SMTP）" value={`${settings.host} : 465 (SSL/TLS)`} />
                <CopyableCode label="ユーザー名" value={settings.username} />
                <p className="text-xs text-muted-foreground">
                  ユーザー名は「メールアドレス全体」です。@ より前だけではログインできません。
                </p>
              </div>
            ) : null}

            <div className="flex flex-wrap justify-end gap-2 pt-2">
              {/*
                **いま作ったアドレスの設定情報シートへ飛ばす。**

                以前は `/mail-clients`（機種別の一般的な手引き）へ飛ばして
                いた。押しても**このアドレスの設定情報は出てこない**ので、
                利用者から見ればボタンが機能していないのと同じだった。
                印刷用の画面は `/print/mail-setup?ids=...` に既にあり、
                アプリのレイアウトの外に置いてある（紙のことを知っているのは
                その画面だけ、という設計。`routes/print-setup-sheet.tsx`）。

                id が取れなかったときは押せなくする。飛ばした先で
                「印刷するメールアドレスが選ばれていません」を見せるより、
                押せないほうが理由が分かる。
              */}
              <Button
                variant="outline"
                disabled={!createdId}
                title={createdId ? undefined : '作成結果の取得に失敗しました。一覧から印刷してください'}
                onClick={() => {
                  onOpenChange(false);
                  navigate({ to: `/print/mail-setup?ids=${encodeURIComponent(createdId ?? '')}` });
                }}
              >
                <Printer aria-hidden />
                設定情報を印刷・保存する
              </Button>
              <Button onClick={() => onOpenChange(false)}>閉じる</Button>
            </div>
          </div>
        ) : null}

      </DialogContent>
    </Dialog>
  );
}
